import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import Swal from 'sweetalert2';

function Salepurchase() {
    const [reportType, setReportType] = useState("Sale");
    const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState(null);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => {
            if (user) setUserId(user.uid);
        });
        return () => unsub();
    }, []);

    const fetchReport = async () => {
        if (!userId) return;
        setLoading(true);
        const collectionName = reportType === "Sale" ? "sales" : "purchases";

        try {
            // 1. Fetch Lubricants for Name Mapping
            const lubQuery = query(collection(db, "lubricants"), where("userId", "==", userId));
            const lubSnap = await getDocs(lubQuery);
            const lubMap = {};
            lubSnap.docs.forEach(doc => {
                lubMap[doc.id] = doc.data().name;
            });

            // 2. Fetch Vendors for Name Mapping (Crucial for Purchase Report)
            const vendorQuery = query(collection(db, "vendors"), where("userId", "==", userId));
            const vendorSnap = await getDocs(vendorQuery);
            const vendorMap = {};
            vendorSnap.docs.forEach(doc => {
                vendorMap[doc.id] = doc.data().name;
            });

            // 3. Fetch main transaction data
            const q = query(
                collection(db, collectionName),
                where("userId", "==", userId),
                where("date", ">=", fromDate),
                where("date", "<=", toDate),
                orderBy("date", "desc")
            );

            const querySnapshot = await getDocs(q);
            let formattedData = [];

            querySnapshot.docs.forEach(doc => {
                const rawData = doc.data();

                if (reportType === "Purchase") {
                    // PURCHASE LOGIC: Flatten the 'items' array
                    if (rawData.items && Array.isArray(rawData.items)) {
                        rawData.items.forEach((item, index) => {
                            formattedData.push({
                                id: `${doc.id}-${index}`,
                                date: rawData.date,
                                billNo: rawData.billNo || 'N/A',
                                vendorName: vendorMap[rawData.vendorId] || 'Unknown Vendor',
                                // Uses itemId from Purchaseentry.js
                                productName: lubMap[item.itemId] || item.itemName || "Unknown Product",
                                qty: parseFloat(item.qty || 0),
                                rate: parseFloat(item.rate || 0),
                                totalAmount: parseFloat(item.total || (item.qty * item.rate) || 0)
                            });
                        });
                    }
                } else {
                    // SALE LOGIC: Flat structure
                    formattedData.push({
                        id: doc.id,
                        date: rawData.date,
                        // Uses productId from sales collection
                        productName: lubMap[rawData.productId] || "Unknown Product",
                        qty: parseFloat(rawData.qty || 0),
                        rate: parseFloat(rawData.rate || 0),
                        totalAmount: parseFloat(rawData.totalAmount || (rawData.qty * rawData.rate) || 0)
                    });
                }
            });

            setDataList(formattedData);
        } catch (error) {
            console.error("Firestore Error:", error);
            Swal.fire('Error', `Failed to generate report`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => window.print();

    return (
        <div className="content-wrapper bg-white min-vh-100 text-dark">
            {/* FILTER HEADER */}
            <section className="content-header no-print p-4 bg-light border-bottom border-dark">
                <div className="row align-items-end">
                    <div className="col-md-2">
                        <label className="small font-weight-bold text-uppercase">Report Type</label>
                        <select
                            className="form-control border-dark font-weight-bold"
                            value={reportType}
                            onChange={(e) => { setReportType(e.target.value); setDataList([]); }}
                        >
                            <option value="Sale">Sale Report</option>
                            <option value="Purchase">Purchase Report</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="small font-weight-bold text-uppercase">From Date</label>
                        <input type="date" className="form-control border-dark" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                        <label className="small font-weight-bold text-uppercase">To Date</label>
                        <input type="date" className="form-control border-dark" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                    </div>
                    <div className="col-md-4">
                        <button className="btn btn-dark px-4 mr-2 font-weight-bold" onClick={fetchReport} disabled={loading}>
                            {loading ? 'LOADING...' : 'GENERATE'}
                        </button>
                        <button className="btn btn-outline-dark font-weight-bold" onClick={handlePrint}>PRINT</button>
                    </div>
                </div>
            </section>

            {/* REPORT CONTENT */}
            <section className="content p-4 report-container bg-white">
                <div className="text-center mb-4 border-bottom border-dark pb-3">
                    <h2 className="font-weight-bold mb-0">SHRIMATI PADMAVATI PETROLEUM</h2>
                    <h5 className="text-uppercase mt-2 font-weight-bold">{reportType} SUMMARY REGISTER</h5>
                    <div className="font-weight-bold">Period: {fromDate} to {toDate}</div>
                </div>

                <div className="table-responsive">
                    <table className="table table-bordered dsr-table-main">
                        <thead className="bg-light">
                            <tr className="text-black border-thick text-center">
                                <th width="120">DATE</th>
                                {reportType === "Purchase" ? (
                                    <>
                                        <th>VENDOR / BILL NO</th>
                                        <th>PRODUCT NAME</th>
                                    </>
                                ) : (
                                    <th>ITEM NAME</th>
                                )}
                                <th>QTY</th>
                                <th>RATE</th>
                                <th>TOTAL AMOUNT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dataList.length > 0 ? dataList.map((row) => (
                                <tr key={row.id} className="text-center text-black">
                                    <td>{row.date}</td>
                                    {reportType === "Purchase" ? (
                                        <>
                                            <td>{row.vendorName} / {row.billNo}</td>
                                            <td className="font-weight-bold">{row.productName}</td>
                                        </>
                                    ) : (
                                        <td className="font-weight-bold">{row.productName}</td>
                                    )}
                                    <td>{row.qty}</td>
                                    <td>₹{row.rate}</td>
                                    <td className="font-weight-bold">₹{row.totalAmount.toLocaleString()}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={reportType === "Purchase" ? 6 : 5} className="text-center py-5">
                                        No data found for the selected period.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {dataList.length > 0 && (
                            <tfoot className="border-thick font-weight-bold bg-light">
                                <tr className="text-center">
                                    <td colSpan={reportType === "Purchase" ? 3 : 1} className="text-right">GRAND TOTAL</td>

                                    {/* Total Quantity */}
                                    <td>
                                        {dataList.reduce((acc, curr) => acc + (parseFloat(curr.qty) || 0), 0).toFixed(2)}
                                    </td>

                                    <td>--</td>

                                    {/* Total Amount */}
                                    <td>
                                        ₹{dataList.reduce((acc, curr) => acc + (parseFloat(curr.totalAmount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

                {/* SIGNATURE SECTION */}
                <div className="only-print mt-5 d-flex justify-content-between px-5 py-4">
                    <div className="text-center font-weight-bold border-top border-dark pt-2" style={{ width: '200px' }}>Prepared By</div>
                    <div className="text-center font-weight-bold border-top border-dark pt-2" style={{ width: '200px' }}>Authorized Sign</div>
                </div>
            </section>

            <style dangerouslySetInnerHTML={{
                __html: `
        .text-black { color: #000 !important; }
        .dsr-table-main { border: 2px solid #000 !important; }
        .dsr-table-main th, .dsr-table-main td { 
          border: 1px solid #000 !important; 
          color: #000 !important;
          vertical-align: middle !important;
          padding: 8px;
        }
        .border-thick { border: 2px solid #000 !important; }
        
        @media print {
          .no-print { display: none !important; }
          .report-container { width: 100%; }
          .table { font-size: 10pt !important; }
          .dsr-table-main th { background-color: #eee !important; -webkit-print-color-adjust: exact; }
          @page { size: portrait; margin: 1cm; }
        }
        .only-print { display: none; }
        @media print { .only-print { display: flex; } }
      `}} />
        </div>
    );
}

export default Salepurchase;